import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Wrench, 
  Droplets, 
  ShowerHead, 
  Flame, 
  Gauge,
  PipetteIcon as Pipe,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

export default function AboutPage() {
  const services = [
    {
      icon: Wrench,
      title: 'Professional Plumbing',
      description: 'Comprehensive plumbing solutions for residential and commercial properties. Expert repairs, installations, and maintenance.'
    },
    {
      icon: ShowerHead,
      title: 'Bathroom & Kitchen Installations',
      description: 'Complete bathroom and kitchen design, installation, and renovation services. From concept to completion with meticulous attention to detail.'
    },
    {
      icon: Flame,
      title: 'Boiler & Heating',
      description: 'Professional boiler installation, servicing, and repairs by Gas Safe registered engineers. Keeping your home warm and safe.'
    },
    {
      icon: Droplets,
      title: 'Leak Detection',
      description: 'Advanced technology to detect and repair leaks quickly, preventing water damage and reducing waste in London homes.'
    },
    {
      icon: Pipe,
      title: 'Pipework Installation',
      description: 'Expert pipework installation and repairs for residential and commercial properties. Quality materials meeting borough standards.'
    },
    {
      icon: Gauge,
      title: 'Water Pressure Solutions',
      description: 'Diagnose and fix water pressure issues. Ensure optimal water flow throughout your property with professional solutions.'
    }
  ];

  const values = [
    {
      title: 'Over 20 Years Experience',
      description: 'Two decades of professional plumbing expertise across London properties.'
    },
    {
      title: 'Borough Partnership',
      description: '10+ years working with London Boroughs and councils on housing improvements.'
    },
    {
      title: 'Safety First',
      description: 'All work meets the highest safety standards to protect London residents.'
    },
    {
      title: 'Quality Workmanship',
      description: 'Certified engineers delivering professional solutions that last.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-muted/30 to-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-16 animate-slide-up">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              About Elite Plumbers
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
              London\'s Trusted{' '}
              <span className="text-primary">Plumbing Partners</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Elite Plumbers is a London-based plumbing company with over 20 years of experience 
              delivering reliable, professional, and high-quality services. For the past decade, 
              we\'ve been proud partners with London Boroughs and councils, making homes across 
              the city safer and more suitable for all residents.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative h-[400px] lg:h-[500px] animate-fade-in">
              <div className="absolute inset-0 bg-primary/5 rounded-3xl" />
              <img
                src="https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg"
                alt="Professional plumber installing tap"
                className="absolute inset-0 w-full h-full object-cover rounded-3xl shadow-xl"
              />
            </div>

            {/* Content */}
            <div className="space-y-6">
              <h2 className="text-3xl font-heading font-bold text-foreground">
                Why Choose Elite Plumbers?
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                With over 20 years of experience serving London, we\'ve built our reputation on 
                quality work, honest pricing, and exceptional service. For the past decade, we\'ve 
                been trusted partners with London Boroughs and councils, helping to improve housing 
                standards and ensure every home is safe and suitable for its residents.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our work with local authorities has given us unique insight into the specific needs 
                of London properties, from historic buildings to modern developments. We understand 
                the importance of compliance, safety, and quality workmanship in every job we undertake.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {values.map((value, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-foreground">{value.title}</h3>
                        <p className="text-sm text-muted-foreground">{value.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow group"
              >
                <Link to="/contact">
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Our Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground">
              Comprehensive Plumbing Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From emergency repairs to complete installations, we handle it all with expertise and care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="p-6 border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300 group cursor-pointer"
                >
                  <div className="space-y-4">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Photo Showcase Section */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
              Our Work in Action
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See the quality and professionalism we bring to every job
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="overflow-hidden border-2 group">
              <div className="relative h-80">
                <img
                  src="https://images.unsplash.com/photo-1723988429049-0a42e45e8501?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwbHVtYmluZyUyMHJlcGFpcnxlbnwwfHx8fDE3Njk4MTk4MDl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional plumbing repair"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 bg-card">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  Precision Repairs
                </h3>
                <p className="text-sm text-muted-foreground">
                  Expert leak detection and repair using the latest tools and techniques
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 group">
              <div className="relative h-80">
                <img
                  src="https://images.unsplash.com/photo-1768724058866-4c33efbd50a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxiYXRocm9vbSUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3Njk4MTg5MjR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern bathroom installation"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 bg-card">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  Modern Installations
                </h3>
                <p className="text-sm text-muted-foreground">
                  Beautiful bathroom and kitchen installations with attention to every detail
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 group">
              <div className="relative h-80">
                <img
                  src="https://images.unsplash.com/photo-1706670359282-e5182f748973?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxiYXRocm9vbSUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3Njk4MTg5MjR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Glass shower installation"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 bg-card">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  Luxury Fixtures
                </h3>
                <p className="text-sm text-muted-foreground">
                  High-end bathroom fixtures installed to perfection
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 group">
              <div className="relative h-80">
                <img
                  src="https://images.unsplash.com/photo-1749532125405-70950966b0e5"
                  alt="Professional plumber at work"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 bg-card">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  Expert Engineers
                </h3>
                <p className="text-sm text-muted-foreground">
                  Certified professionals with years of experience
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold">
            Ready to Experience Professional Plumbing?
          </h2>
          <p className="text-lg text-secondary-foreground/80">
            Contact us today for a quote. Our team has over 20 years of experience 
            making London homes safer and more suitable for everyone.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow"
            >
              <Link to="/contact">Get a Quote</Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/10"
            >
              <Link to="/coverage-areas">View Coverage Areas</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}